const REGION_ORDER = ['US', 'Europe', 'Canada', 'America', 'Asia', 'Indian', 'Antarctica', 'Arctic', 'Pacific', 'Mexico', 'Africa', 'Atlantic', 'Australia', 'UTC'];

// Overrides the IANA region prefix for specific representatives, splitting
// the broad "America" bucket into country-level groups. UTC has no region
// prefix in its IANA ID so it needs an explicit override.
const ZONE_REGION_OVERRIDES = {
  UTC: 'UTC',
  'Pacific/Honolulu': 'US',
  'America/Adak': 'US',
  'America/Anchorage': 'US',
  'America/Phoenix': 'US',
  'America/Los_Angeles': 'US',
  'America/Denver': 'US',
  'America/Chicago': 'US',
  'America/New_York': 'US',
  'America/St_Johns': 'Canada',
  'America/Halifax': 'Canada',
  'America/Whitehorse': 'Canada',
  'America/Mexico_City': 'Mexico',
  'America/Hermosillo': 'Mexico',
  'America/Cancun': 'Mexico'
};

// When multiple zones share the same generic name and offset, these IANA IDs
// are preferred as the representative value — they're the well-known reference
// cities that other systems (OS pickers, APIs) commonly use.
const PREFERRED_ZONES = new Set(['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'America/Phoenix', 'America/Halifax', 'America/St_Johns', 'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Santiago', 'America/Mexico_City', 'America/Hermosillo', 'America/Cancun', 'America/Lima', 'America/Bogota', 'America/Caracas', 'America/Manaus', 'America/La_Paz', 'America/Asuncion', 'America/Montevideo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Amsterdam', 'Europe/Rome', 'Europe/Madrid', 'Europe/Zurich', 'Europe/Stockholm', 'Europe/Helsinki', 'Europe/Athens', 'Europe/Moscow', 'Europe/Istanbul', 'Europe/Kiev',
// renamed to Kyiv in IANA 2022f; keep both until all runtimes update
'Europe/Kyiv', 'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Casablanca', 'Africa/Abidjan', 'Africa/Bangui', 'Africa/Blantyre', 'Africa/Addis_Ababa', 'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka', 'Asia/Bangkok', 'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Jakarta', 'Asia/Hong_Kong', 'Asia/Taipei', 'Asia/Tehran', 'Asia/Jerusalem', 'Asia/Riyadh', 'Asia/Baghdad', 'Asia/Kabul', 'Asia/Tashkent', 'Asia/Kathmandu', 'Asia/Yangon', 'Asia/Kuala_Lumpur', 'Asia/Manila', 'Asia/Vladivostok', 'Asia/Kamchatka', 'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Guam', 'Pacific/Tongatapu', 'Pacific/Apia', 'Pacific/Port_Moresby', 'Pacific/Honolulu', 'Australia/Sydney', 'Australia/Adelaide', 'Australia/Darwin', 'Australia/Perth', 'Australia/Brisbane', 'Atlantic/Reykjavik', 'Atlantic/Azores', 'Atlantic/Cape_Verde', 'UTC']);
function formatOffset(offsetMinutes) {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const minutes = String(abs % 60).padStart(2, '0');
  return `UTC ${sign}${hours}:${minutes}`;
}
function parseOffsetMinutes(shortOffset) {
  var _match$;
  const match = shortOffset.match(/GMT([+-])(\d+)(?::(\d+))?/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt((_match$ = match[3]) !== null && _match$ !== void 0 ? _match$ : '0', 10);
  return sign * (hours * 60 + minutes);
}
function getGenericName(ianaId, locale, now) {
  var _ianaId$split$pop;
  try {
    var _parts$find;
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: ianaId,
      timeZoneName: 'longGeneric'
    }).formatToParts(now);
    const name = (_parts$find = parts.find(p => p.type === 'timeZoneName')) === null || _parts$find === void 0 ? void 0 : _parts$find.value;
    if (name) return name;
  } catch (_unused) {
    // fall through to city fallback
  }
  const city = (_ianaId$split$pop = ianaId.split('/').pop()) !== null && _ianaId$split$pop !== void 0 ? _ianaId$split$pop : ianaId;
  return city.replace(/_/g, ' ');
}
function getOffsetMinutes(ianaId, now) {
  try {
    var _parts$find$value, _parts$find2;
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaId,
      timeZoneName: 'shortOffset'
    }).formatToParts(now);
    const offsetStr = (_parts$find$value = (_parts$find2 = parts.find(p => p.type === 'timeZoneName')) === null || _parts$find2 === void 0 ? void 0 : _parts$find2.value) !== null && _parts$find$value !== void 0 ? _parts$find$value : 'GMT+0';
    return parseOffsetMinutes(offsetStr);
  } catch (_unused2) {
    return 0;
  }
}
function preferredFirst(a, b) {
  const aPreferred = PREFERRED_ZONES.has(a.value) ? 0 : 1;
  const bPreferred = PREFERRED_ZONES.has(b.value) ? 0 : 1;
  return aPreferred - bPreferred || a.value.localeCompare(b.value);
}
function compute(locale) {
  const now = new Date();
  const allIds = Intl.supportedValuesOf('timeZone');

  // Collect all candidates, keyed by (genericName, offset) for deduplication.
  // Sub-region zones (e.g. America/Indiana/Indianapolis) are included as
  // candidates so they can win if listed in PREFERRED_ZONES, but they lose
  // to top-level zones otherwise.
  const groups = new Map();
  for (const ianaId of allIds) {
    if (!ianaId.includes('/') || ianaId.startsWith('Etc/')) continue;
    const offsetMinutes = getOffsetMinutes(ianaId, now);
    const genericName = getGenericName(ianaId, locale, now);
    const dedupeKey = `${genericName}|${offsetMinutes}`;
    const label = `${formatOffset(offsetMinutes)} ${genericName}`;
    if (!groups.has(dedupeKey)) groups.set(dedupeKey, []);
    groups.get(dedupeKey).push({
      value: ianaId,
      label,
      offsetMinutes
    });
  }

  // UTC is not enumerated by Intl.supportedValuesOf('timeZone') in all runtimes,
  // but it is a valid IANA ID that the BE may send. Inject it explicitly so the
  // picker can show a selection when context.timezone === 'UTC'.
  const utcLabel = `${formatOffset(0)} ${getGenericName('UTC', locale, now)}`;
  groups.set('UTC', [{
    value: 'UTC',
    label: utcLabel,
    offsetMinutes: 0
  }]);

  // For each group, pick the single best representative.
  const byRegion = new Map();
  for (const candidates of groups.values()) {
    var _ZONE_REGION_OVERRIDE;
    candidates.sort(preferredFirst);
    const representative = candidates[0];
    const region = (_ZONE_REGION_OVERRIDE = ZONE_REGION_OVERRIDES[representative.value]) !== null && _ZONE_REGION_OVERRIDE !== void 0 ? _ZONE_REGION_OVERRIDE : representative.value.split('/')[0];
    if (!byRegion.has(region)) byRegion.set(region, []);
    byRegion.get(region).push(representative);
  }
  for (const options of byRegion.values()) {
    options.sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.label.localeCompare(b.label));
  }
  const knownRegions = REGION_ORDER.filter(r => byRegion.has(r));
  const otherRegions = [...byRegion.keys()].filter(r => !REGION_ORDER.includes(r)).sort();
  return [...knownRegions, ...otherRegions].map(region => ({
    region,
    options: byRegion.get(region).map(({
      value,
      label
    }) => ({
      value,
      label
    }))
  }));
}
const MAX_CACHE_ENTRIES = 4;
const cache = new Map();
export function buildTimezoneGroups(locale) {
  // Include the UTC date in the cache key so labels refresh after DST transitions.
  // A session open across a midnight boundary will see updated offsets on the
  // next call (e.g. spring-forward: old labels valid until ~2am, then refreshed).
  const dateKey = new Date().toISOString().slice(0, 10);
  const cacheKey = `${locale}|${dateKey}`;
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }
  if (cache.size >= MAX_CACHE_ENTRIES) {
    cache.delete(cache.keys().next().value);
  }
  const groups = compute(locale);
  cache.set(cacheKey, groups);
  return groups;
}